import '@/assets/svg';
import { Component, useEffect } from 'react';
import { Provider } from 'mobx-react';
import * as Sentry from '@sentry/react';
import store from '@/store';
import commonLocales from '&/locales';
import locales from '@/locales';
import PreviewMedial from '@/pages/commonPages/previewMediaGlobal';
import { AtMessage } from 'taro-ui';
import Taro from '@tarojs/taro';
import '@/utils/formEntries';
import { handleLogin, isIOSSystem } from '@/utils/common';
import { entryUrlStr, enUSStr, zhCNStr } from '@/constants';
import ParamsObserver from '@/components/ParamsObserver';
import '@/assets/icon/bold/iconfont.css';
import '@/assets/icon/outline/iconfont.css';
import { merge } from 'lodash';
import offlineImg from '@/assets/img/offline.png';
import './taroui-variables.scss';
import './app.scss';

let requireAll = requireContext => requireContext.keys().map(requireContext);
let svgs = require.context('&/assets/svgicon', false, /\.svg$/);
requireAll(svgs);

const OFFLINE_MASK_ID = 'global-offline-mask-portal';
const OFFLINE_MASK_HTML = `
    <div class="global-offline-mask__body">
        <img class="global-offline-mask__icon" src="${offlineImg}" alt="offline" />
        <div class="global-offline-mask__desc">网络请求失败</div>
        <button class="global-offline-mask__btn" type="button">刷新</button>
    </div>
`;

const ensureOfflineMaskElement = () => {
    if (typeof document === 'undefined') return null;
    let el = document.getElementById(OFFLINE_MASK_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = OFFLINE_MASK_ID;
        el.className = 'global-offline-mask global-offline-mask--hidden';
        el.innerHTML = OFFLINE_MASK_HTML;
        document.body.appendChild(el);
    }
    return el;
};

window.addEventListener(
    'unhandledrejection',
    event => {
        console.warn(
            `[UNHANDLED PROMISE REJECTION]: ${JSON.stringify(event.reason)}`
        );
    },
    true
);

(function SentryInit() {
    if (process.env.NODE_ENV === 'production') {
        Sentry.init({
            dsn: 'https://f5659de557fb451b4fafcd828c841346@sentry-cn.foxitsoftware.cn/6', //'https://b4ac6bb076344f9b9b1e9ab3b82e7033@sentry.foxithd.com:443/3',
            integrations: [
                Sentry.reactRouterV6BrowserTracingIntegration({
                    useEffect,
                }),
                Sentry.replayIntegration({
                    //stickySession: false,
                    networkDetailAllowUrls: [window.location.origin],
                    mutationBreadcrumbLimit: 30,
                    mutationLimit: 50,
                    maskAllText: false,
                    blockAllMedia: false,
                    maskAllInputs: false,
                }),
                // new BrowserTracing(),
                // new Replay({
                //     // Additional SDK configuration goes in here, for example:
                //     networkDetailAllowUrls: [window.location.origin],
                //     mutationBreadcrumbLimit: 30,
                //     mutationLimit: 50,
                //     maskAllText: false,
                //     blockAllMedia: false,
                //     maskAllInputs: false,
                // }),
            ],

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            tracesSampleRate: 0,
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            maxBreadcrumbs: 8,
            normalizeMaxBreadth: 30,
            // release:
            //     process.env.REACT_APP_VERSION +
            //     '-' +
            //     process.env.REACT_APP_ENV_TAG,
            environment: process.env.REACT_APP_ENV_TAG,
            // dist: '2024-12-5',
            autoSessionTracking: false,
            beforeSend(event) {
                if (isNeedFilter(event)) {
                    return null;
                }
                return event;
            },
            ignoreErrors: [
                // Random plugins/extensions
                'top.GLOBALS',
                // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
                'originalCreateNotification',
                'canvas.contentDocument',
                'MyApp_RemoveAllHighlights',
                'http://tt.epicplay.com',
                "Can't find variable: ZiteReader",
                'jigsaw is not defined',
                'ComboSearch is not defined',
                'http://loading.retry.widdit.com/',
                'atomicFindClose',
                // Facebook borked
                'fb_xd_fragment',
                // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
                // reduce this. (thanks @acdha)
                // See http://stackoverflow.com/questions/4113268
                'bmi_SafeAddOnload',
                'EBCallBackMessageReceived',
                // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
                'conduitPage',
            ],
            denyUrls: [
                // Facebook flakiness
                /graph\.facebook\.com/i,
                // Facebook blocked
                /connect\.facebook\.net\/en_US\/all\.js/i,
                // Woopra flakiness
                /eatdifferent\.com\.woopra-ns\.com/i,
                /static\.woopra\.com\/js\/woopra\.js/i,
                // Chrome extensions
                /extensions\//i,
                /^chrome:\/\//i,
                // Other plugins
                /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
                /webappstoolbarba\.texthelp\.com\//i,
                /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            ],
        });
    }
})();

function isNeedFilter(event) {
    let ret = false;

    if (event.request.url.indexOf('http://localhost:45678') === 0) {
        ret = true;
    } else if (
        event?.extra?.__serialized__ &&
        (event.extra.__serialized__.code === 401 ||
            event.extra.__serialized__.message?.[0] === 600)
    ) {
        console.log('zx isNeedFilter', event.extra.__serialized__);
        ret = true;
    }

    return ret;
}

if (window.location.href.includes('zxtest=1')) {
    let flag = new Date().getTime();
    Sentry.withScope(function (scope) {
        scope.setFingerprint(['{{ default }}', 'zxtest-12-5-', flag]);
        Sentry.captureException(new Error('zxtest-12-5-' + flag));
    });
    // throw new Error('zxtest-11-12'+ new Date().getTime());
}

class App extends Component {
    state = {
        showContent: false,
        isOffline: !window.navigator.onLine,
    };

    componentDidMount() {
        const { lang } = store.appStore;
        handleLogin(() => {
            this.setState({
                showContent: true,
            });
        });
        initI18(lang);
        saveEntryUrl();
        this.offlineMaskEl = null;
        this.initOfflineMask();
        this.registerNetworkListeners();
    }

    componentWillUnmount() {
        this.unregisterNetworkListeners();
    }

    componentDidShow() {
        if (window.location.host === 'm.foxithd.com') {
            return;
        }

        try {
            new window.VConsole();
        } catch (error) {
            console.log('vvvv', error);
        }
    }

    componentDidHide() { }

    componentDidCatchError(err, errInfo) {
        console.log('error', err, errInfo);
    }

    initOfflineMask() {
        const el = ensureOfflineMaskElement();
        if (!el) return;
        const refreshBtn = el.querySelector('.global-offline-mask__btn');
        if (refreshBtn) {
            refreshBtn.removeEventListener('click', this.handleRefresh);
            refreshBtn.addEventListener('click', this.handleRefresh);
        }
        this.offlineMaskEl = el;
        if (this.state.isOffline) {
            this.showOfflineMask();
        } else {
            this.hideOfflineMask();
        }
    }

    showOfflineMask() {
        if (this.offlineMaskEl) {
            this.offlineMaskEl.classList.remove('global-offline-mask--hidden');
        }
    }

    hideOfflineMask() {
        if (this.offlineMaskEl) {
            this.offlineMaskEl.classList.add('global-offline-mask--hidden');
        }
    }

    updateOfflineState = flag => {
        if (flag) {
            this.showOfflineMask();
        } else {
            this.hideOfflineMask();
        }
        if (this.state.isOffline !== flag) {
            this.setState({ isOffline: flag });
        }
    };

    handleResourceError = event => {
        const target = event?.target;
        console.log('app.js handleResourceError event=', event, target);
        if (!target) return;
        const tag = target.tagName;
        if (tag === 'SCRIPT' || tag === 'LINK') {
            this.updateOfflineState(true);
        }
    };

    handleChunkRejection = event => {
        const reason = event?.reason;
        console.log('app.js handleChunkRejection event=', event, reason);
        if (!reason) return;
        const message = reason?.message || '';
        if (
            reason?.name === 'ChunkLoadError' ||
            message.includes('Loading chunk') ||
            message.includes('ChunkLoadError')
        ) {
            this.updateOfflineState(true);
        }
    };

    registerNetworkListeners() {
        this.routerChangeHandler = this.handleRouterChange;
        Taro.eventCenter.on('__taroRouterChange', this.routerChangeHandler);
        this.syncInitialNetworkState();
        window.addEventListener('error', this.handleResourceError, true);
        window.addEventListener('unhandledrejection', this.handleChunkRejection);
    }

    unregisterNetworkListeners() {
        if (this.routerChangeHandler) {
            Taro.eventCenter.off('__taroRouterChange', this.routerChangeHandler);
        }
        window.removeEventListener('error', this.handleResourceError, true);
        window.removeEventListener('unhandledrejection', this.handleChunkRejection);
    }

    async syncInitialNetworkState() {
        try {
            const { networkType } = await Taro.getNetworkType();
            const offline = networkType === 'none';
            this.updateOfflineState(offline);
        } catch (_err) {
            this.updateOfflineState(!window.navigator.onLine);
        }
    }

    // 断网页白名单，这些路径不显示断网遮罩
    offlineWhitelist = [];

    isInOfflineWhitelist = () => {
        const path = window.location.pathname || '';
        return this.offlineWhitelist.some(item => path.includes(item));
    };

    handleRouterChange = () => {
        if (this.isInOfflineWhitelist()) {
            this.updateOfflineState(false);
            return;
        }
        const offline = !window.navigator.onLine;
        this.updateOfflineState(offline);
    };

    handleRefresh = () => {
        if (window.navigator.onLine) {
            window.location.reload();
            return;
        }
        Taro.showToast({
            title: '网络异常，请稍后再试',
            duration: 3 * 1e3,
            icon: 'none',
        });
        this.updateOfflineState(true);
    };

    // this.props.children 就是要渲染的页面
    render() {
        const { showContent } = this.state;
        return showContent ? (
            <Provider {...store}>
                <ParamsObserver />
                {this.props.children}
                <PreviewMedial
                    onClose={() => store.previewMedia.toggleMediaVisible(false)}
                />
                <AtMessage />
            </Provider>
        ) : null;
    }
}

function initI18(lang) {
    //intl test
    intl.init({
        currentLocale: lang,
        locales: {
            // 递归深合并
            // 如果遇到相同KEY的值是简单数据类型，后面会覆盖前面；如果值是对象则合并
            [enUSStr]: merge(commonLocales[enUSStr], locales[enUSStr]),
            [zhCNStr]: merge(commonLocales[zhCNStr], locales[zhCNStr]),
        },
    });

    return lang;
}

function saveEntryUrl() {
    if (isIOSSystem()) {
        localStorage.setItem(entryUrlStr, window.location.href.split('#')[0]);
    }
}

export default App;
